import React, { useState, useCallback, useEffect, useRef } from 'react';
import { LoiFormSection } from '../../slices/marketplace/loi-form/loi-form-section';
import { LegalIssueItem } from '../../slices/marketplace/loi-form/legal-issue-item';
import { LoiForm } from '../../slices/marketplace/loi-form/loi-form';
import { LoiFormMode } from '../../slices/marketplace/loi-form/loi-form-mode.enum';
import { getOrderedItemsForSection } from '../../slices/marketplace/loi-form/get-ordered-items-for-section';
import { TermForm } from '../../slices/marketplace/loi-form/term-form';
import { hasValueForLinkedField } from '../../slices/marketplace/loi-form/has-value-for-linked-field';
import { isLegalIssueVisibleByLogic } from '../../slices/marketplace/loi-form/legal-issue-visibility';
import { renderLoiAnswerReadonly } from '../../slices/marketplace/loi-form/render-loi-answer-readonly';
import { renderLoiAnswerEditable } from '../../slices/marketplace/loi-form/render-loi-answer-editable';
import InfoTooltip from '../info-tooltip/info-tooltip';
import CollapsibleComponent from '../Collapsible/CollapsibleComponent';
import './LegalIssuesForm.scss';

export interface LegalIssuesFormProps {
    legalIssueSections: LoiFormSection[];
    legalIssues: LegalIssueItem[];
    termForm?: TermForm | null;
    onChange?: (payload: LoiForm) => void;
    mode?: LoiFormMode;
    readOnly?: boolean;
    displayAsLabels?: boolean;
    invalidItemIds?: Set<string>;
}

export const LegalIssuesForm = (props: LegalIssuesFormProps) => {
    const {
        legalIssueSections: initialSections,
        legalIssues: initialIssues,
        termForm,
        onChange,
        mode,
        readOnly = false,
        displayAsLabels = false,
        invalidItemIds,
    } = props;
    const isAdminViewMode = mode === LoiFormMode.AdminDefaults;

    const sectionsToDisplay =
        termForm != null && mode === LoiFormMode.LoiSubmissionEvent
            ? initialSections.filter((s) => {
                  const linked = s.termsFormLinkedField;
                  if (linked == null) return true;
                  return hasValueForLinkedField(termForm, linked);
              })
            : initialSections;

    const [legalIssues, setLegalIssues] =
        useState<LegalIssueItem[]>(initialIssues);
    const [openSectionId, setOpenSectionId] = useState<string | null>(null);
    const skipSyncFromPropsRef = useRef(false);

    useEffect(() => {
        if (skipSyncFromPropsRef.current) {
            skipSyncFromPropsRef.current = false;
            return;
        }
        setLegalIssues(initialIssues);
    }, [initialIssues]);

    const notifyChange = useCallback(
        (next: LegalIssueItem[]) => {
            skipSyncFromPropsRef.current = true;
            queueMicrotask(() => {
                onChange?.({
                    legalIssueSections: initialSections,
                    legalIssues: next,
                });
            });
        },
        [initialSections, onChange]
    );

    const handleValueChange = useCallback(
        (itemId: string, value: string | null) => {
            setLegalIssues((prev) => {
                const next = prev.map((item) =>
                    item.id === itemId ? { ...item, value } : item
                );
                notifyChange(next);
                return next;
            });
        },
        [notifyChange]
    );

    const renderValueInput = (item: LegalIssueItem) =>
        renderLoiAnswerEditable({
            value: item.value,
            answerType: item.answerType,
            options: item.options,
            ariaLabel: item.name,
            disabled: readOnly,
            invalid: invalidItemIds?.has(item.id) ?? false,
            onChange: (value) => handleValueChange(item.id, value),
        });

    const renderValueReadOnly = (item: LegalIssueItem) =>
        renderLoiAnswerReadonly(item.value, item.answerType, item.options);

    const sectionsOrdered = [...sectionsToDisplay].sort(
        (a, b) => a.order - b.order
    );

    return (
        <div
            className={`loi-form${mode === LoiFormMode.BuyerProfile ? ' loi-form--buyer' : ''}`}>
            <div className="loi-form-grid loi-form-grid-header">
                <div className="loi-form-row loi-form-row-head">
                    <div className="loi-form-cell loi-form-cell-name">
                        Legal Issue
                    </div>
                    <div className="loi-form-cell loi-form-cell-value">
                        {isAdminViewMode ? 'Default Value' : 'Value'}
                    </div>
                    <div className="loi-form-cell loi-form-cell-guidance">
                        Guidance
                    </div>
                </div>
            </div>
            {sectionsOrdered.map((section) => {
                const items = getOrderedItemsForSection(
                    section.id,
                    legalIssues
                );
                if (items.length === 0) return null;
                const isOpen = openSectionId === section.id;
                const sectionHasError =
                    invalidItemIds != null &&
                    items.some(
                        (item) =>
                            invalidItemIds.has(item.id) &&
                            (isAdminViewMode ||
                                isLegalIssueVisibleByLogic(legalIssues, item))
                    );
                return (
                    <CollapsibleComponent
                        key={section.id}
                        isExpanded={isOpen}
                        onToggle={(expanded) =>
                            setOpenSectionId(expanded ? section.id : null)
                        }
                        validationError={sectionHasError}
                        header={section.name}>
                        <div className="loi-form-section-body">
                            <div className="loi-form-grid">
                                {
                                    items.reduce<{
                                        currentHeaderLevel: number;
                                        nodes: React.ReactNode[];
                                    }>(
                                        (acc, item) => {
                                            if (!isAdminViewMode && !isLegalIssueVisibleByLogic(legalIssues, item)) {
                                                return acc;
                                            }
                                            const level = item.headerLevel;
                                            if (level != null && level >= 1) {
                                                acc.currentHeaderLevel = level;
                                            }
                                            const rowClass =
                                                level === 1
                                                    ? 'loi-form-row-header level-1'
                                                    : level != null &&
                                                        level >= 2
                                                      ? `loi-form-row-subheader level-${Math.min(level, 5)}`
                                                      : 'loi-form-row-item';
                                            const itemIndentLevel = Math.min(
                                                acc.currentHeaderLevel,
                                                5
                                            );
                                            const itemUnderClass =
                                                rowClass === 'loi-form-row-item'
                                                    ? ` item-under-${itemIndentLevel}`
                                                    : '';
                                            const showValueInput =
                                                item.showValueInput !== false;
                                            acc.nodes.push(
                                                <div
                                                    key={item.id}
                                                    className={`loi-form-row ${rowClass}${itemUnderClass}`}>
                                                    <div className="loi-form-cell loi-form-cell-name">
                                                        <div className="loi-form-cell-name-with-tooltip">
                                                            <span>{item.name}</span>
                                                            {item.tooltip ? (
                                                                <InfoTooltip
                                                                    text={item.tooltip}
                                                                    idText={`loi-tooltip-${item.id}`}
                                                                    className="loi-form-name-tooltip"
                                                                />
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                    <div className="loi-form-cell loi-form-cell-value">
                                                        {isAdminViewMode || displayAsLabels
                                                            ? renderValueReadOnly(item)
                                                            : showValueInput
                                                              ? renderValueInput(item)
                                                              : null}
                                                    </div>
                                                    <div className="loi-form-cell loi-form-cell-guidance">
                                                        {item.guidance ? (
                                                            <span className="loi-form-guidance-plain">
                                                                {item.guidance}
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            );
                                            return acc;
                                        },
                                        { currentHeaderLevel: 1, nodes: [] }
                                    ).nodes
                                }
                            </div>
                        </div>
                    </CollapsibleComponent>
                );
            })}
        </div>
    );
};

export default LegalIssuesForm;
